import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { VideoData } from '@lumen5/luminary';
import { z } from 'zod';

import { resolveAssetUuidFromRegistryId } from '../registries/resolveVoiceoverId';
import { VoiceoverRegistry } from '../registries/VoiceoverRegistry';
import type { ToolExecutionContext } from '../types';

import { newSceneGroupBaseSceneNote } from './base-scene-note';
import { ConversationTool } from './ConversationTool';
import { computeSceneGroupShortIds, resolveInsertSlotAfter } from './scene-group-addressing';

import { locateSegment, parseSegmentRequest, precheckSegment } from './segment-request';

import { applyPlaybackRegionsToSceneGroupOperation } from 'js/src/data/clip-creator/clip-creator-operations';
import { checkpointVideoOperation } from 'js/src/data/video-data';
import { toImmutableAudioAsset } from 'js/src/luminary/immutable-media-source-data';
import type { RootState } from 'js/src/libs/RootState';
import {
    createNewVoicedrivenSceneGroupOperation,
    deleteSceneGroupOperation,
    setAndApplyVoiceoverAssetForSceneGroupOperation,
} from 'js/src/luminary';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';

export const addRecordedVoiceoverSceneGroupSchema = z.object({
    after_scene_group_id: z
        .string()
        .optional()
        .describe(
            'The stable scene_group_id of the scene group the new voiceover scene group should be inserted immediately after. Omit to insert it at the very start of the video.',
        ),
    audio_id: z.string().describe('The id of the uploaded voiceover audio file.'),
    language_code: z
        .string()
        .optional()
        .describe(
            'Optional language code for caption generation (e.g., "en", "es"). Auto-detected if omitted.',
        ),
    words: z
        .array(z.string())
        .min(1)
        .optional()
        .describe(
            'Include only part of the recording: the exact transcript words of the segment to keep, in order. The segment spans those words, so it starts and ends on a word boundary. Omit to include the whole recording. For a long segment, pass start_quote and end_quote instead.',
        ),
    start_quote: z
        .string()
        .optional()
        .describe(
            `Instead of words: the first few words of the segment to keep, copied exactly as the transcript spells them (e.g. a ${TOOL_NAMES.proposeClips} segment or a clip_select answer). Quote enough words that they occur only once in the recording. Pass with end_quote.`,
        ),
    end_quote: z
        .string()
        .optional()
        .describe(
            'Instead of words: the last few words of the segment to keep, copied exactly as the transcript spells them. Pass with start_quote.',
        ),
});
export type AddRecordedVoiceoverSceneGroupArgs = z.infer<
    typeof addRecordedVoiceoverSceneGroupSchema
>;

export class AddRecordedVoiceoverSceneGroupTool extends ConversationTool<
    typeof addRecordedVoiceoverSceneGroupSchema
> {
    protected override readonly schema = addRecordedVoiceoverSceneGroupSchema;
    protected override readonly toolName = TOOL_NAMES.addRecordedVoiceoverSceneGroup;
    public override readonly subagentAccess = SubagentAccess.PLACEMENT;
    protected override readonly toolDescription =
        'Add a new voiceover scene group driven by an uploaded voiceover AUDIO file (e.g. a human recording). The audio becomes the narration track and its speech is transcribed into a synced transcript.';

    public override getToolDisplayName(): string {
        return 'Adding voiceover scene group';
    }

    public get statusIcon(): string {
        return 'mic';
    }

    public async execute(args: AddRecordedVoiceoverSceneGroupArgs, context: ToolExecutionContext) {
        const { after_scene_group_id, audio_id, language_code, words, start_quote, end_quote } =
            args;
        const { parentActionGroup } = context;

        const segment = parseSegmentRequest({ words, start_quote, end_quote });
        if (segment && 'error' in segment) {
            return `Error: ${segment.error}`;
        }

        // Resolve the insert slot up front (fail-closed on an unknown anchor id).
        const slot = resolveInsertSlotAfter(this.luminary, after_scene_group_id);

        const voiceoverRegistry = VoiceoverRegistry.getInstance();

        // The id resolves straight to its audio asset, covering both sources the agent has:
        // a chat attachment and a voiceover search result.
        const voiceoverAsset = voiceoverRegistry.getAudioAsset(audio_id);

        if (!voiceoverAsset) {
            // A media id resolves too — that means a video (e.g. a talking head), which is
            // another tool's job. Name it so the agent redirects instead of retrying blindly.
            if (resolveAssetUuidFromRegistryId(audio_id)) {
                return `Error: ${audio_id} is not an audio file. For an uploaded talking-head video, use ${TOOL_NAMES.addTalkingHeadSceneGroup} instead.`;
            }
            return `Error: Could not find an uploaded audio file with id ${audio_id}. Make sure the audio file was attached to the chat and finished uploading, or was returned by ${TOOL_NAMES.searchUploadedVoiceovers}.`;
        }
        if (!voiceoverAsset.has_voiceover) {
            return `Error: The uploaded audio does not appear to contain spoken voiceover. Please upload an audio file with clear speech.`;
        }

        if (segment) {
            const precheckError = await precheckSegment(audio_id, segment);
            if (precheckError) {
                return `Error: ${precheckError}, so nothing was added. Copy the words exactly as the transcript spells them.`;
            }
        }

        try {
            const {
                createNewVoicedrivenSceneGroup,
                setAndApplyVoiceoverAssetForSceneGroup,
                deleteSceneGroup,
                checkpointVideo,
                applyPlaybackRegionsToSceneGroup,
            } = makeOperationsCallable(
                {
                    createNewVoicedrivenSceneGroupOperation,
                    setAndApplyVoiceoverAssetForSceneGroupOperation,
                    deleteSceneGroupOperation,
                    checkpointVideoOperation,
                    applyPlaybackRegionsToSceneGroupOperation,
                },
                this.luminary,
                this.dispatch,
                this.getState as () => RootState,
            );

            const { sceneGroupId } = createNewVoicedrivenSceneGroup(
                VideoData.CATEGORY_VOICEOVER,
                slot,
                { deleteOtherScenes: false, setPlaceholderAsset: false, hasAvatar: false },
                parentActionGroup,
                false,
            );
            context.subagentScope?.claimSceneGroup(sceneGroupId);

            // Applies the uploaded audio as the voiceover. The transcript is omitted so
            // the operation fetches (or generates) it from the audio asset itself.
            // recommendOverlays is false — the agent adds overlays explicitly via
            // add_voiceover_scene. This operation checkpoints the video itself.
            const hasAssetBeenApplied = await setAndApplyVoiceoverAssetForSceneGroup({
                sceneGroupId,
                audioAssetData: toImmutableAudioAsset(voiceoverAsset),
                avatarAssetData: null,
                captionsLanguage: language_code ?? null,
                forceCaptionGeneration: false,
                selectedBlueprintId: null,
                generateRecommendedVideoResponse: null,
                sceneRecommendations: null,
                recommendOverlays: false,
                isGlobalVideo: true,
                systemTriggered: false,
                parentActionGroup,
            });

            const cleanupSceneGroup = (): boolean => {
                try {
                    deleteSceneGroup(sceneGroupId, false, parentActionGroup);
                } catch {
                    return false;
                }
                checkpointVideo({ systemTriggered: false, parentActionGroup });
                return true;
            };

            if (!hasAssetBeenApplied) {
                cleanupSceneGroup();
                return `Error: the voiceover audio could not be applied to the new scene group.`;
            }

            let keptWordSpan: { startTimeMs: number; endTimeMs: number } | undefined;
            if (segment) {
                const createdSceneGroup = this.luminary.getSceneGroupById(sceneGroupId);
                const transcript = createdSceneGroup?.originalTranscript;
                const transcriptWords =
                    transcript?.transcriptData?.words || transcript?.words || [];
                const match = createdSceneGroup
                    ? locateSegment(transcriptWords, segment)
                    : { error: 'the new scene group could not be found' };
                if ('error' in match || !createdSceneGroup) {
                    const removed = cleanupSceneGroup();
                    const reason = 'error' in match ? match.error : 'the segment was not found';
                    return removed
                        ? `Error: ${reason}, so there is no segment to keep. Nothing was added. Copy the words exactly as they appear in the transcript.`
                        : `Error: ${reason}. The scene group was added covering the full recording and is the only one in the video, so it could not be removed automatically — replace it or retry with words from the transcript.`;
                }
                keptWordSpan = {
                    startTimeMs: transcriptWords[match.startIndex].startTime,
                    endTimeMs: transcriptWords[match.endIndex].endTime,
                };
                applyPlaybackRegionsToSceneGroup(createdSceneGroup, [
                    [keptWordSpan.startTimeMs, keptWordSpan.endTimeMs],
                ]);
                checkpointVideo({ systemTriggered: false, parentActionGroup });
            }

            this.startBaseSceneBuilding(sceneGroupId);

            const durationSeconds = voiceoverAsset.duration;
            const durationInfo =
                typeof durationSeconds === 'number' && !keptWordSpan
                    ? ` Voiceover duration: ${durationSeconds.toFixed(1)}s.`
                    : '';

            const shortId =
                computeSceneGroupShortIds(this.luminary.sceneGroups.map((group) => group.id)).get(
                    sceneGroupId,
                ) ?? sceneGroupId;
            const placement =
                after_scene_group_id && after_scene_group_id.trim() !== ''
                    ? `right after scene group ${after_scene_group_id}`
                    : 'at the start of the video';
            const trimNote = keptWordSpan
                ? ` covering ${Math.round(keptWordSpan.startTimeMs)}ms\u2013${Math.round(keptWordSpan.endTimeMs)}ms of the recording`
                : '';
            return `Success: Added new voiceover scene group ${shortId} ${placement} from uploaded audio${trimNote}.${durationInfo}${newSceneGroupBaseSceneNote()}`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error adding voiceover scene group: ${errorMessage}`;
        }
    }
}
